import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import '../index.css';

const Blog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, user } = useContext(AuthContext);
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState(['All']);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    category: '',
    image: '📝',
    authorName: 'GamersUnion'
  });

  useEffect(() => {
    fetchArticles();
    // Check if we're navigating back with an article to edit
    if (location.state?.editArticle) {
      handleEdit(location.state.editArticle);
    }
  }, []);

  useEffect(() => {
    // Filter articles by category
    if (selectedCategory === 'All') {
      setFilteredArticles(articles);
    } else {
      setFilteredArticles(articles.filter(article => article.category === selectedCategory));
    }
  }, [selectedCategory, articles]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/blogs');
      setArticles(response.data);
      
      // Extract unique categories
      const uniqueCategories = ['All', ...new Set(response.data.map(article => article.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingArticle(null);
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      category: '',
      image: '📝',
      authorName: 'GamersUnion'
    });
    setShowEditor(true);
    setShowPreview(false);
  };

  const insertFormatting = (before, after = '') => {
    const textarea = document.getElementById('content-textarea');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    const newContent = formData.content.substring(0, start) + before + selectedText + after + formData.content.substring(end);
    setFormData({ ...formData, content: newContent });
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, end + before.length);
    }, 0);
  };

  const insertEmoji = (emoji) => {
    const textarea = document.getElementById('content-textarea');
    const start = textarea.selectionStart;
    const newContent = formData.content.substring(0, start) + emoji + formData.content.substring(start);
    setFormData({ ...formData, content: newContent });
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + emoji.length, start + emoji.length);
    }, 0);
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      category: article.category,
      image: article.image || '📝',
      authorName: article.authorName || 'GamersUnion'
    });
    setShowEditor(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/blogs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchArticles();
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('Failed to delete blog post');
    }
  };

  const extractBodyContent = (html) => {
    // If it's a full HTML document, extract just the body content
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    if (bodyMatch) {
      return bodyMatch[1].trim();
    }
    return html;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      // Extract body content if it's a full HTML document
      const processedContent = extractBodyContent(formData.content);
      
      const dataToSubmit = {
        ...formData,
        content: processedContent
      };
      
      if (editingArticle) {
        await axios.put(`/api/blogs/${editingArticle._id}`, dataToSubmit, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/blogs', dataToSubmit, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setShowEditor(false);
      fetchArticles();
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Failed to save blog post');
    }
  };

  const toolbarButtonStyle = {
    padding: '6px 10px',
    backgroundColor: 'var(--card-bg)',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    color: 'var(--text)',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    minWidth: '32px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  // Editor View
  if (showEditor) {
    return (
      <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '40px 20px' }}>
        <button onClick={() => setShowEditor(false)} style={{
          padding: '10px 20px', backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)',
          borderRadius: '8px', color: 'var(--text)', cursor: 'pointer', fontSize: '14px', marginBottom: '30px'
        }}>← Back to Blog</button>

        <div style={{
          backgroundColor: 'var(--card-bg)', borderRadius: '16px', padding: '40px', border: '1px solid var(--border)'
        }}>
          <h2 style={{ color: 'var(--text-accent)', marginBottom: '30px', fontSize: '28px' }}>
            {editingArticle ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h2>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>Title</label>
              <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required style={{
                  width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)',
                  backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '16px'
                }} />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>Excerpt</label>
              <textarea value={formData.excerpt} onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                required rows="3" style={{
                  width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)',
                  backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '16px', fontFamily: 'inherit', resize: 'vertical'
                }} />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <label style={{ color: 'var(--text)', fontWeight: '600' }}>Content</label>
                <button type="button" onClick={() => setShowPreview(!showPreview)} style={{
                  padding: '6px 16px', backgroundColor: 'var(--primary)', color: 'white', border: 'none',
                  borderRadius: '6px', fontSize: '13px', cursor: 'pointer'
                }}>
                  {showPreview ? '📝 Edit' : '👁️ Preview'}
                </button>
              </div>

              {/* Formatting Toolbar */}
              <div style={{
                display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '12px',
                backgroundColor: 'var(--card-bg-secondary)', borderRadius: '8px 8px 0 0',
                border: '1px solid var(--border)', borderBottom: 'none'
              }}>
                <button type="button" onClick={() => insertFormatting('<h3>', '</h3>')} style={toolbarButtonStyle} title="Heading">
                  <strong>H3</strong>
                </button>
                <button type="button" onClick={() => insertFormatting('<h4>', '</h4>')} style={toolbarButtonStyle} title="Subheading">
                  <strong>H4</strong>
                </button>
                <span style={{ borderLeft: '1px solid var(--border)', margin: '0 4px' }}></span>
                <button type="button" onClick={() => insertFormatting('<strong>', '</strong>')} style={toolbarButtonStyle} title="Bold">
                  <strong>B</strong>
                </button>
                <button type="button" onClick={() => insertFormatting('<em>', '</em>')} style={toolbarButtonStyle} title="Italic">
                  <em>I</em>
                </button>
                <button type="button" onClick={() => insertFormatting('<u>', '</u>')} style={toolbarButtonStyle} title="Underline">
                  <u>U</u>
                </button>
                <span style={{ borderLeft: '1px solid var(--border)', margin: '0 4px' }}></span>
                <button type="button" onClick={() => insertFormatting('<p>', '</p>')} style={toolbarButtonStyle} title="Paragraph">
                  ¶
                </button>
                <button type="button" onClick={() => insertFormatting('<br/>')} style={toolbarButtonStyle} title="Line Break">
                  ↵
                </button>
                <span style={{ borderLeft: '1px solid var(--border)', margin: '0 4px' }}></span>
                <button type="button" onClick={() => insertFormatting('<a href="URL">', '</a>')} style={toolbarButtonStyle} title="Link">
                  🔗
                </button>
                <span style={{ borderLeft: '1px solid var(--border)', margin: '0 4px' }}></span>
                <button type="button" onClick={() => insertEmoji('😊')} style={toolbarButtonStyle} title="Happy">😊</button>
                <button type="button" onClick={() => insertEmoji('🎮')} style={toolbarButtonStyle} title="Gaming">🎮</button>
                <button type="button" onClick={() => insertEmoji('🚀')} style={toolbarButtonStyle} title="Rocket">🚀</button>
                <button type="button" onClick={() => insertEmoji('💡')} style={toolbarButtonStyle} title="Idea">💡</button>
                <button type="button" onClick={() => insertEmoji('⭐')} style={toolbarButtonStyle} title="Star">⭐</button>
                <button type="button" onClick={() => insertEmoji('🔥')} style={toolbarButtonStyle} title="Fire">🔥</button>
                <button type="button" onClick={() => insertEmoji('✨')} style={toolbarButtonStyle} title="Sparkles">✨</button>
                <button type="button" onClick={() => insertEmoji('👍')} style={toolbarButtonStyle} title="Thumbs Up">👍</button>
              </div>

              {showPreview ? (
                <div style={{
                  minHeight: '400px', padding: '20px', borderRadius: '0 0 8px 8px', border: '1px solid var(--border)',
                  backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '16px', lineHeight: '1.8',
                  overflowY: 'auto', maxHeight: '600px'
                }} dangerouslySetInnerHTML={{ __html: extractBodyContent(formData.content) }} />
              ) : (
                <textarea 
                  id="content-textarea"
                  value={formData.content} 
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required 
                  rows="15" 
                  placeholder="Write your content here... You can use the toolbar above to format text and add emojis!"
                  style={{
                    width: '100%', padding: '12px', borderRadius: '0 0 8px 8px', border: '1px solid var(--border)',
                    backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '14px', fontFamily: 'inherit', 
                    resize: 'vertical', lineHeight: '1.6'
                  }} 
                />
              )}
              
              <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                💡 Tip: You can paste full HTML documents (including &lt;html&gt;, &lt;head&gt;, &lt;body&gt; tags) and the body content will be automatically extracted. Or use the toolbar for quick formatting!
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>Category</label>
                <input type="text" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required placeholder="e.g., Developer Tips" style={{
                    width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)',
                    backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '16px'
                  }} />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>Icon/Emoji</label>
                <input type="text" value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="📝" style={{
                    width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)',
                    backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '16px'
                  }} />
              </div>
            </div>

            <div style={{ marginBottom: '30px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text)', fontWeight: '600' }}>Author Name</label>
              <input type="text" value={formData.authorName} onChange={(e) => setFormData({ ...formData, authorName: e.target.value })}
                style={{
                  width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)',
                  backgroundColor: 'var(--card-bg)', color: 'var(--text)', fontSize: '16px'
                }} />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="submit" style={{
                padding: '12px 32px', backgroundColor: 'var(--primary)', color: 'white', border: 'none',
                borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer'
              }}>{editingArticle ? 'Update Post' : 'Publish Post'}</button>
              <button type="button" onClick={() => setShowEditor(false)} style={{
                padding: '12px 32px', backgroundColor: 'transparent', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer'
              }}>Cancel</button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
      <div style={{ textAlign: 'center', marginBottom: '50px', animation: 'fadeInUp 0.6s ease-out' }}>
        <h1 style={{
          color: 'var(--text-accent)', fontSize: '42px', fontWeight: '800', marginBottom: '16px',
          textTransform: 'uppercase', letterSpacing: '2px'
        }}>GamersUnion Blog</h1>
        <p style={{
          color: 'var(--text-secondary)', fontSize: '18px', lineHeight: '1.6', maxWidth: '700px', margin: '0 auto'
        }}>Insights, guides, and resources for indie developers and gamers</p>

        {isAdmin && (
          <button onClick={handleCreate} style={{
            marginTop: '24px', padding: '12px 32px', backgroundColor: 'var(--primary)', color: 'white',
            border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
          }}>+ Create New Post</button>
        )}
      </div>

      {/* Category Filter */}
      {categories.length > 1 && (
        <div style={{
          display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap',
          marginBottom: '40px', animation: 'fadeInUp 0.6s ease-out 0.1s both'
        }}>
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              style={{
                padding: '8px 20px',
                backgroundColor: selectedCategory === category ? 'var(--primary)' : 'var(--card-bg)',
                color: selectedCategory === category ? 'white' : 'var(--text)',
                border: `2px solid ${selectedCategory === category ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: '20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
              onMouseEnter={(e) => {
                if (selectedCategory !== category) {
                  e.target.style.borderColor = 'var(--primary)';
                  e.target.style.color = 'var(--primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedCategory !== category) {
                  e.target.style.borderColor = 'var(--border)';
                  e.target.style.color = 'var(--text)';
                }
              }}
            >
              {category}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
          Loading blog posts...
        </div>
      ) : filteredArticles.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
          <h3 style={{ color: 'var(--text-accent)', marginBottom: '12px' }}>
            {selectedCategory === 'All' ? 'No blog posts yet' : `No posts in "${selectedCategory}"`}
          </h3>
          <p style={{ color: 'var(--text-secondary)' }}>
            {isAdmin ? 'Click "Create New Post" to add your first blog post!' : 'Check back later for new content!'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '30px' }}>
          {filteredArticles.map((article, index) => (
            <div key={article._id} style={{
              backgroundColor: 'var(--card-bg)', borderRadius: '16px', padding: '30px', border: '1px solid var(--border)',
              cursor: 'pointer', transition: 'all 0.3s ease', animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
              display: 'flex', flexDirection: 'column'
            }} onClick={() => navigate(`/blog/${article._id}`)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.2)';
                e.currentTarget.style.borderColor = 'var(--primary)';
              }} onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = 'var(--border)';
              }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', textAlign: 'center' }}>{article.image}</div>
              
              <span style={{
                display: 'inline-block', padding: '4px 12px', backgroundColor: 'var(--primary)', color: 'white',
                borderRadius: '16px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase',
                letterSpacing: '1px', marginBottom: '16px', alignSelf: 'flex-start'
              }}>{article.category}</span>

              <h2 style={{
                color: 'var(--text-accent)', fontSize: '22px', fontWeight: '700',
                marginBottom: '12px', lineHeight: '1.4', flex: '0 0 auto'
              }}>{article.title}</h2>

              <p style={{
                color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6',
                marginBottom: '20px', flex: '1 1 auto'
              }}>{article.excerpt}</p>

              <div style={{
                display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '16px',
                borderTop: '1px solid var(--border)', fontSize: '13px', color: 'var(--text-secondary)', flex: '0 0 auto'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{new Date(article.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                  <span style={{ color: 'var(--primary)', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    Read More →
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    ❤️ {article.likesCount || 0}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    💬 {article.commentsCount || 0}
                  </span>
                </div>
              </div>

              {isAdmin && (
                <div style={{ marginTop: '16px', display: 'flex', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
                  <button onClick={(e) => { e.stopPropagation(); handleEdit(article); }} style={{
                    padding: '6px 12px', backgroundColor: 'var(--primary)', color: 'white', border: 'none',
                    borderRadius: '6px', cursor: 'pointer', fontSize: '12px'
                  }}>Edit</button>
                  <button onClick={(e) => { e.stopPropagation(); handleDelete(article._id); }} style={{
                    padding: '6px 12px', backgroundColor: '#ff4757', color: 'white', border: 'none',
                    borderRadius: '6px', cursor: 'pointer', fontSize: '12px'
                  }}>Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <style jsx>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Blog;
